/**
 * Product Registry
 *
 * Central registry for all products. Each product exports:
 * - config: Product metadata, branding, services
 * - funnel: Resolved funnel configuration (defaults + overrides)
 * - root: Product-specific root component
 * - routes: Product-specific route implementations
 */

import type {FunnelConfig} from './funnel'
import {resolveFunnel} from './funnel'

export const products = {
  narcis: {
    config: () => import('../../products/narcis/config').then(m => m.narcisConfig),
    funnel: () => import('../../products/narcis/config').then(m => resolveFunnel(m.narcisConfig.funnel)),
  },
  quies: {
    config: () => import('../../products/quies/config').then(m => m.quiesConfig),
    funnel: () => import('../../products/quies/config').then(m => resolveFunnel(m.quiesConfig.funnel)),
  },
  ovid: {
    config: () => import('../../products/ovid/config').then(m => m.ovidConfig),
    funnel: () => import('../../products/ovid/config').then(m => resolveFunnel(m.ovidConfig.funnel)),
  },
  toile: {
    config: () => import('../../products/toile/config').then(m => m.toileConfig),
    funnel: () => import('../../products/toile/config').then(m => resolveFunnel(m.toileConfig.funnel)),
  },
}

export type ProductId = keyof typeof products

export function getProductConfig() {
  const productId = getProductId()

  if (!(productId in products)) {
    throw new Error(`Unknown product: ${productId}. Available: ${Object.keys(products).join(', ')}`)
  }

  return products[productId]
}

// The set of valid product ids, derived from `products` (single source of truth) rather than
// hand-maintained — adding a product to `products` above extends validation automatically.
const VALID_PRODUCT_IDS = new Set(Object.keys(products)) as Set<string>

export function getProductId(): ProductId {
  const id = process.env.PRODUCT_NAME
  if (!id) {
    if (process.env.ENV === 'production') {
      throw new Error('PRODUCT_NAME environment variable is required in production')
    }
    return 'narcis'
  }
  // Wave 1 hardening (2026-07-11): validate in ALL envs, not just when PRODUCT_NAME is absent in
  // prod. A typo'd PRODUCT_NAME used to cast straight through and land every DB write's `product`
  // column value on whatever string was typed — now a Postgres enum backstops the column, but
  // failing here (before any query runs) gives a clear error instead of a P2032 enum-mismatch
  // deep in a repository call.
  if (!VALID_PRODUCT_IDS.has(id)) {
    throw new Error(`Unknown PRODUCT_NAME: "${id}". Valid products: ${Array.from(VALID_PRODUCT_IDS).join(', ')}`)
  }
  return id as ProductId
}

export async function getProductFunnel(): Promise<FunnelConfig> {
  return getProductConfig().funnel()
}
